import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { api, type SensorWriteReq, type Zone } from "../api";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  // When editing, pass sensorId. The form loads the current sensor +
  // interfaces from the API.
  sensorId?: number;
}

type IfaceRow = SensorWriteReq["interfaces"][number] & { key: number };

// Form values mirror SensorWriteReq but keep the multi-zone list as a
// single `zone_ids` field; we split primary out at submit time.
type FormValues = Omit<SensorWriteReq, "zone_id" | "zone_ids" | "interfaces"> & {
  zone_ids: number[];
};

export default function SensorForm({ open, onClose, onSaved, sensorId }: Props) {
  const [form] = Form.useForm<FormValues>();
  const [zones, setZones] = useState<Zone[]>([]);
  const [ifaces, setIfaces] = useState<IfaceRow[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setZones(await api.zones());
      } catch (e) {
        message.error(String(e));
      }
      if (sensorId) {
        try {
          const d = await api.sensor(sensorId);
          // Pre-populate multi-select: prefer zone_ids from server, fall
          // back to the legacy single zone_id when the junction is empty.
          const zoneIDs =
            d.sensor.zone_ids && d.sensor.zone_ids.length > 0
              ? d.sensor.zone_ids
              : [d.sensor.zone_id];
          form.setFieldsValue({
            name: d.sensor.name,
            enabled: d.sensor.enabled,
            listen_ip: d.sensor.listen_ip,
            listen_port: d.sensor.listen_port,
            protocol: d.sensor.protocol as SensorWriteReq["protocol"],
            exporter_ip: d.sensor.exporter_ip,
            zone_ids: zoneIDs,
            spool_dir: "/opt/sflow/spool",
          });
          setIfaces(
            d.interfaces.map((x, i) => ({
              key: i,
              if_index: x.if_index,
              name: x.name,
              direction: (x.direction || "auto") as "auto" | "in" | "out",
              color: x.color,
            }))
          );
        } catch (e) {
          message.error(String(e));
        }
      } else {
        form.resetFields();
        form.setFieldsValue({
          enabled: true,
          listen_ip: "0.0.0.0",
          listen_port: 6343,
          protocol: "sflow",
          exporter_ip: "0.0.0.0",
          spool_dir: "/opt/sflow/spool",
        });
        setIfaces([]);
      }
    })();
  }, [open, sensorId, form]);

  async function onOk() {
    try {
      const values = await form.validateFields();
      const ids = values.zone_ids ?? [];
      if (ids.length === 0) {
        message.error("Pick at least one IP Zone");
        return;
      }
      // First selection becomes the primary (sensor.ipzone_id). The rest
      // are tracked in the sensor_ipzone junction. Backend accepts both
      // fields and deduplicates.
      const { zone_ids: _omit, ...restValues } = values;
      const body: SensorWriteReq = {
        ...restValues,
        zone_id: ids[0],
        zone_ids: ids,
        interfaces: ifaces.map(({ key: _k, ...ifRest }) => ifRest),
      };
      setBusy(true);
      if (sensorId) {
        await api.updateSensor(sensorId, body);
      } else {
        await api.createSensor(body);
      }
      message.success(sensorId ? "Sensor updated" : "Sensor created");
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  function addIface() {
    setIfaces((xs) => [
      ...xs,
      {
        key: Date.now(),
        if_index: (xs[xs.length - 1]?.if_index ?? 0) + 1,
        name: "",
        direction: "auto",
        color: "",
      },
    ]);
  }

  function removeIface(key: number) {
    setIfaces((xs) => xs.filter((x) => x.key !== key));
  }

  function patchIface(key: number, patch: Partial<IfaceRow>) {
    setIfaces((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  }

  const ifaceColumns: ColumnsType<IfaceRow> = [
    {
      title: "if_index",
      dataIndex: "if_index",
      width: 110,
      render: (v: number, row) => (
        <InputNumber
          min={0}
          value={v}
          onChange={(n) => patchIface(row.key, { if_index: Number(n ?? 0) })}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (v: string, row) => (
        <Input
          value={v}
          placeholder="eth0, WAN, …"
          onChange={(e) => patchIface(row.key, { name: e.target.value })}
        />
      ),
    },
    {
      title: "Direction",
      dataIndex: "direction",
      width: 140,
      render: (v: string, row) => (
        <Select
          value={v}
          style={{ width: 120 }}
          options={[
            { value: "auto", label: "auto" },
            { value: "in", label: "in" },
            { value: "out", label: "out" },
          ]}
          onChange={(val) =>
            patchIface(row.key, { direction: val as "auto" | "in" | "out" })
          }
        />
      ),
    },
    {
      title: "Color",
      dataIndex: "color",
      width: 130,
      render: (v: string, row) => (
        <Input
          type="color"
          value={v || "#888888"}
          onChange={(e) => patchIface(row.key, { color: e.target.value })}
          style={{ width: 60, padding: 2 }}
        />
      ),
    },
    {
      title: "",
      width: 60,
      render: (_: unknown, row) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          danger
          onClick={() => removeIface(row.key)}
        />
      ),
    },
  ];

  // 2-column form layout matching Wansight's "Flow Sensor Configuration"
  // modal: right-aligned narrow label column, field column to the right,
  // two pairs per row.
  const labelCol = { span: 9 };
  const wrapperCol = { span: 15 };

  return (
    <Modal
      open={open}
      title={
        sensorId ? `Flow Sensor Configuration — #${sensorId}` : "Add sFlow source"
      }
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={busy}
      width={780}
      okText="Save"
    >
      <Form<FormValues>
        form={form}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
        labelAlign="right"
        colon={true}
        size="small"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Sensor Name"
              rules={[{ required: true }]}
            >
              <Input placeholder="edge-sensor-01" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="listen_ip"
              label="Listener IP"
              rules={[{ required: true }]}
            >
              <Input placeholder="0.0.0.0" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="listen_port"
              label="Listener Port"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={65535} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="protocol"
              label="Flow Protocol"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: "sflow", label: "sFlow v5" },
                  { value: "nfv5", label: "NetFlow v5" },
                  { value: "nfv9", label: "NetFlow v9" },
                  { value: "ipfix", label: "IPFIX" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="exporter_ip"
              label="Exporter IP"
              rules={[{ required: true }]}
            >
              <Input placeholder="0.0.0.0 (any)" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="zone_ids"
              label="IP Zones"
              tooltip="First zone selected is the primary zone (stored in sensor.ipzone_id). Flows match against the union of all selected zones."
              rules={[
                {
                  required: true,
                  validator: (_r, v: number[] | undefined) =>
                    v && v.length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error("pick at least one zone")),
                },
              ]}
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="pick one or more zones"
                options={zones.map((z) => ({
                  value: z.id,
                  label: `${z.id} — ${z.name}`,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="spool_dir" label="Spool Dir">
              <Input placeholder="/opt/sflow/spool" />
            </Form.Item>
          </Col>
        </Row>

        <fieldset
          style={{
            marginTop: 8,
            border: "1px solid #d0d0d0",
            padding: 0,
          }}
        >
          <legend
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: "0 6px",
              marginLeft: 8,
              color: "#2a2d33",
            }}
          >
            Monitored Interfaces
          </legend>
          <div
            style={{
              padding: 6,
              background: "#f5f5f5",
              borderBottom: "1px solid #d0d0d0",
            }}
          >
            <Button size="small" icon={<PlusOutlined />} onClick={addIface}>
              Add Interface
            </Button>
          </div>
          <Table
            rowKey="key"
            size="small"
            dataSource={ifaces}
            columns={ifaceColumns}
            pagination={false}
            locale={{ emptyText: "No interfaces — Add Interface to insert one." }}
          />
        </fieldset>
      </Form>
    </Modal>
  );
}
